import { COLUMN_PRESETS, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { FieldTypeEnum } from '../custom-field.types'
import { ModuleColumnDefinition } from '../module'

/**
 * Columns for the org-wide Client Authorizations list (SD-123). Every column
 * is read-only: authorizations are edited from their client, where the scope
 * pickers (project/contract/billing profile) live. `percentUsed` is derived
 * on the client from consumed / authorized. Deliberately NOT registered in
 * STANDARD_COLUMNS_BY_MODULE: client authorizations support no custom fields,
 * calculated fields or generic imports.
 */
export const BILLING_AUTHORIZATION_STANDARD_COLUMNS: ModuleColumnDefinition[] =
  [
    {
      id: 'label',
      name: 'label',
      label: 'Authorization',
      description: 'Client authorization label',
      isVisible: true,
      isRequired: true,
      isSystem: true,
      isFixed: true,
      isReadOnly: true,
      order: 0,
      fieldType: FieldTypeEnum.enum.STRING,
      ...COLUMN_PRESETS.NAME,
    },
    {
      id: 'entityName',
      name: 'entityName',
      label: 'Client',
      description: 'Client the authorization belongs to',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 1,
      fieldType: FieldTypeEnum.enum.STRING,
      ...STANDARD_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'projectName',
      name: 'projectName',
      label: 'Project',
      description: 'Project scope (blank = client-wide)',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 2,
      fieldType: FieldTypeEnum.enum.STRING,
      ...STANDARD_COLUMN_WIDTHS.WIDE,
    },
    {
      id: 'referenceNumber',
      name: 'referenceNumber',
      label: 'Reference',
      description: 'PO or reference number',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 3,
      fieldType: FieldTypeEnum.enum.STRING,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'authorizationType',
      name: 'authorizationType',
      label: 'Type',
      description: 'Kind of authorization',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 4,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...COLUMN_PRESETS.STATUS,
    },
    {
      id: 'status',
      name: 'status',
      label: 'Status',
      description: 'Authorization status',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 5,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      ...COLUMN_PRESETS.STATUS,
    },
    {
      id: 'authorizedAmount',
      name: 'authorizedAmount',
      label: 'Authorized',
      description: 'Authorized amount (blank = unlimited)',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 6,
      fieldType: FieldTypeEnum.enum.CURRENCY,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'consumedAmount',
      name: 'consumedAmount',
      label: 'Consumed',
      description: 'Invoices, time, expenses and imported balance',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 7,
      fieldType: FieldTypeEnum.enum.CURRENCY,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'remainingAmount',
      name: 'remainingAmount',
      label: 'Remaining',
      description: 'Authorized minus consumed',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 8,
      fieldType: FieldTypeEnum.enum.CURRENCY,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'percentUsed',
      name: 'percentUsed',
      label: '% Used',
      description: 'Consumed as a share of the authorized amount',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 9,
      fieldType: FieldTypeEnum.enum.NUMBER,
      ...STANDARD_COLUMN_WIDTHS.NARROW,
    },
    {
      id: 'expirationDate',
      name: 'expirationDate',
      label: 'Expires',
      description: 'Expiration date',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 10,
      fieldType: FieldTypeEnum.enum.DATE,
      ...COLUMN_PRESETS.DUE_DATE,
    },
    {
      id: 'ownerName',
      name: 'ownerName',
      label: 'Owner',
      description: 'Member accountable for the authorization',
      isVisible: true,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 11,
      fieldType: FieldTypeEnum.enum.STRING,
      ...COLUMN_PRESETS.OWNER,
    },
    {
      id: 'forecastAmount',
      name: 'forecastAmount',
      label: 'Forecast',
      description: 'Billing scheduled against the authorization',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 12,
      fieldType: FieldTypeEnum.enum.CURRENCY,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'consumedInvoiceAmount',
      name: 'consumedInvoiceAmount',
      label: 'Invoiced',
      description: 'Invoiced amount attributed to the authorization',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 13,
      fieldType: FieldTypeEnum.enum.CURRENCY,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM,
    },
    {
      id: 'effectiveDate',
      name: 'effectiveDate',
      label: 'Effective',
      description: 'Effective date',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 14,
      fieldType: FieldTypeEnum.enum.DATE,
      ...COLUMN_PRESETS.DUE_DATE,
    },
    {
      id: 'updatedAt',
      name: 'updatedAt',
      label: 'Updated',
      description: 'Last change',
      isVisible: false,
      isRequired: false,
      isSystem: true,
      isFixed: false,
      isReadOnly: true,
      order: 99,
      fieldType: FieldTypeEnum.enum.DATETIME,
      ...COLUMN_PRESETS.UPDATED_DATE,
    },
  ]
